import { useMutation } from "@tanstack/react-query";

import {
  cartAttributesUpdateMutation,
  cartBuyerIdentityUpdateMutation,
  cartCreateInputMutation,
  cartCreateMutation,
  cartDiscountCodesUpdateMutation,
  cartLinesAddMutation,
  cartLinesRemoveMutation,
  cartLinesUpdateMutation,
  cartQuery,
} from "../graphql/cart-gql";
import { useGraphQLClient } from "../graphql/client";
import { locale } from "../locales";
import type {
  ICart,
  ICartAttributesUpdateMutation,
  ICartBuyerIdentityUpdateMutation,
  ICartCreateInputMutation,
  ICartCreateMutation,
  ICartDiscountCodesUpdateMutation,
  ICartLinesAddMutation,
  ICartLinesRemoveMutation,
  ICartLinesUpdateMutation,
} from "../types/api/cart";
import type { ICustomerQuery } from "../types/api/customer";
import { ToastType } from "../types/enum";
import type { CartUserError } from "../types/generated/storefront.types";
import { CartErrorCode } from "../types/generated/storefront.types";
import { getMergedCartLines } from "../utils/cart";
import { logShopifyUserError } from "../utils/monitoring";

interface IUseCartQuery {
  lang: "ko" | "en" | "jp";
  cart: ICart["cart"];
  updateCart: (cart: ICart["cart"]) => void;
  customer: ICustomerQuery["customer"];
  accessToken?: string | null;
  openToast: (toast: any) => void;
  onSessionExpired?: () => void;
}

export default function useCartQuery({ lang, cart, updateCart, customer, accessToken, openToast, onSessionExpired }: IUseCartQuery) {
  const request = useGraphQLClient();

  const notifyCartErrors = (operation: string, userErrors: CartUserError[]) => {
    userErrors.forEach((error) => {
      const messages: Record<string, string> = locale(lang).cart_error;
      openToast({ type: ToastType.Error, message: (error.code && messages[error.code]) || messages.DEFAULT });
    });

    logShopifyUserError(operation, userErrors);
  };

  /** buyerIdentity 에 넘긴 customerAccessToken 이 무효라고 응답한 경우 */
  const hasInvalidTokenError = (userErrors: CartUserError[]) =>
    userErrors.some((error) => error.code === CartErrorCode.Invalid && error.field?.includes("customerAccessToken"));

  /** 카트 조회 */
  const getCartMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICart>(cartQuery, variables),
    onSuccess: (data) => {
      if (data.cart) updateCart(data.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 카트 최초 생성 (변수 없음) */
  const createCartMutation = useMutation({
    mutationFn: () => request<ICartCreateMutation>(cartCreateMutation),
    onSuccess: (data) => {
      if (data.cartCreate.userErrors.length) {
        notifyCartErrors("CartCreate", data.cartCreate.userErrors);
        return;
      }

      if (data.cartCreate.cart) {
        updateCart(data.cartCreate.cart);
      } else {
        void getCartMutation.mutateAsync({ id: cart.id });
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 카트 생성 */
  const createCartInputMutation = useMutation({
    // cartCreate는 넘긴 라인을 역순으로 돌려주므로 여기서 뒤집어 보내 새 카트에서도 같은 순서가 유지되게 함
    mutationFn: (variables: Record<string, any>) =>
      request<ICartCreateInputMutation>(
        cartCreateInputMutation,
        Array.isArray(variables?.input?.lines)
          ? { ...variables, input: { ...variables.input, lines: [...variables.input.lines].reverse() } }
          : variables,
      ),
    onSuccess: (data: ICartCreateMutation, variables) => {
      if (data.cartCreate.userErrors.length) {
        // 토큰이 무효면 세션 만료로 처리(로그아웃)한 뒤 비로그인 카트로 다시 만든다.
        // 로그아웃 없이 재시도만 하면 customer 는 남고 카트에는 buyerIdentity 가 없어
        // App 의 불일치 검사가 영영 풀리지 않는다.
        if (hasInvalidTokenError(data.cartCreate.userErrors)) {
          if (onSessionExpired) onSessionExpired();
          else notifyCartErrors("CartCreate", data.cartCreate.userErrors);

          void createCartInputMutation.mutateAsync({ input: { lines: variables?.input?.lines } });
          return;
        }

        notifyCartErrors("CartCreate", data.cartCreate.userErrors);
        return;
      }

      if (data.cartCreate.cart) {
        updateCart(data.cartCreate.cart);
      } else {
        void getCartMutation.mutateAsync({ id: cart.id });
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 만료된 카트 라인 + 실패한 inputLines를 병합해 새 카트를 생성한다 */
  const recreateCartFromExpired = (inputLines: unknown) => {
    const mergedLines = getMergedCartLines({ cart, customer, inputLines: inputLines as any, type: "update" });

    void createCartInputMutation.mutateAsync({
      input: { lines: mergedLines, ...(accessToken && { buyerIdentity: { customerAccessToken: accessToken } }) },
    });
  };

  /** 카트 아이템 수량 업데이트 */
  const updateCartLineMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICartLinesUpdateMutation>(cartLinesUpdateMutation, variables),
    onSuccess: (data, variables) => {
      if (data.cartLinesUpdate.userErrors.length) {
        notifyCartErrors("CartLinesUpdate", data.cartLinesUpdate.userErrors);

        const isExpired = data.cartLinesUpdate.userErrors.some(
          (error) => error.code === CartErrorCode.Invalid && error.field?.includes("cartId"),
        );
        if (isExpired) recreateCartFromExpired(variables?.lines);

        return;
      }

      if (data.cartLinesUpdate.cart) updateCart(data.cartLinesUpdate.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 카트 아이템 제거 */
  const removeCartLineMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICartLinesRemoveMutation>(cartLinesRemoveMutation, variables),
    onSuccess: (data, variables) => {
      if (data.cartLinesRemove.userErrors.length) {
        notifyCartErrors("CartLinesRemove", data.cartLinesRemove.userErrors);

        const isExpired = data.cartLinesRemove.userErrors.some(
          (error) => error.code === CartErrorCode.Invalid && error.field?.includes("cartId"),
        );
        if (isExpired) recreateCartFromExpired(variables?.lines);
        return;
      }

      if (data.cartLinesRemove.cart) updateCart(data.cartLinesRemove.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 카트 아이템 추가 */
  const addCartLineMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICartLinesAddMutation>(cartLinesAddMutation, variables),
    onSuccess: (data, variables) => {
      if (data.cartLinesAdd.userErrors.length) {
        notifyCartErrors("CartLinesAdd", data.cartLinesAdd.userErrors);

        const isExpired = data.cartLinesAdd.userErrors.some(
          (error) => error.code === CartErrorCode.Invalid && error.field?.includes("cartId"),
        );
        if (isExpired) recreateCartFromExpired(variables?.lines);

        return;
      }

      if (data.cartLinesAdd.cart) updateCart(data.cartLinesAdd.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 카트 할인 코드 제어 */
  const discountCartMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICartDiscountCodesUpdateMutation>(cartDiscountCodesUpdateMutation, variables),
    onSuccess: (data) => {
      if (data.cartDiscountCodesUpdate.userErrors.length) {
        notifyCartErrors("CartDiscountCodesUpdate", data.cartDiscountCodesUpdate.userErrors);
        return;
      }

      if (data.cartDiscountCodesUpdate.cart) updateCart(data.cartDiscountCodesUpdate.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 카트 구매자 정보 업데이트 */
  const setBuyerIdentityMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICartBuyerIdentityUpdateMutation>(cartBuyerIdentityUpdateMutation, variables),
    onSuccess: (data) => {
      if (data.cartBuyerIdentityUpdate.userErrors.length) {
        // buyerIdentity 를 붙이지 못한 채 customer 만 남으면 불일치가 풀리지 않는다
        if (hasInvalidTokenError(data.cartBuyerIdentityUpdate.userErrors) && onSessionExpired) {
          onSessionExpired();
          return;
        }

        notifyCartErrors("CartBuyerIdentityUpdate", data.cartBuyerIdentityUpdate.userErrors);
        return;
      }

      if (data.cartBuyerIdentityUpdate.cart) updateCart(data.cartBuyerIdentityUpdate.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const updateCartAttributesMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICartAttributesUpdateMutation>(cartAttributesUpdateMutation, variables),
    onSuccess: (data) => {
      if (data.cartAttributesUpdate.userErrors.length) {
        notifyCartErrors("CartAttributesUpdate", data.cartAttributesUpdate.userErrors);
        return;
      }

      if (data.cartAttributesUpdate.cart) updateCart(data.cartAttributesUpdate.cart);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  return {
    createCart: createCartMutation.mutateAsync,
    createCartInput: createCartInputMutation.mutateAsync,
    createCartInputLoading: createCartInputMutation.isPending,
    updateCartLine: updateCartLineMutation.mutateAsync,
    updateCartLineLoading: updateCartLineMutation.isPending,
    removeCartLine: removeCartLineMutation.mutateAsync,
    removeCartLineLoading: removeCartLineMutation.isPending,
    addCartLine: addCartLineMutation.mutateAsync,
    addCartLineLoading: addCartLineMutation.isPending,
    getCart: getCartMutation.mutateAsync,
    getCartLoading: getCartMutation.isPending,
    discountCart: discountCartMutation.mutateAsync,
    discountCartLoading: discountCartMutation.isPending,
    setBuyerIdentity: setBuyerIdentityMutation.mutateAsync,
    setBuyerIdentityLoading: setBuyerIdentityMutation.isPending,
    updateCartAttributes: updateCartAttributesMutation.mutateAsync,
    updateCartAttributesLoading: updateCartAttributesMutation.isPending,
  };
}
